import type { NavigateTargetKind, TriggerDraft } from "~/api/opsTypes";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The small label beside a navigate target — what KIND of thing "Go" would open (S1/S3/S4). */
export function kindLabel(kind: NavigateTargetKind, t: Translate): string {
  switch (kind) {
    case "research":
      return t("Research");
    case "collection":
      return t("Collection");
    case "work":
      return t("Work item");
    default:
      return t("Document");
  }
}

/**
 * `missing[]` → the sentence a person can act on.
 *
 * The primary button is disabled whenever the server reported ANY gap, so without these the
 * card is a dead end with no explanation — which is exactly what kms's navigate branch was
 * (§E6.1: `missing:["targetQuery"]` rendered nothing at all). Warnings are additive; several
 * can show at once.
 */
export function missingWarnings(
  missing: string[],
  actionId: string | null,
  t: Translate,
): string[] {
  const seen = new Set(missing);
  const lines: string[] = [];

  if (seen.has("targetDocumentId") && actionId === "create-digest") {
    lines.push(t("I couldn't match a target document — open Task Center to pick one."));
  }
  if (seen.has("documentId")) {
    lines.push(t("Open a document first — this action attaches to the page you're on."));
  }
  if (seen.has("whenUtc")) {
    lines.push(t('I couldn\'t work out when. Try "tomorrow 9am" or "next Monday".'));
  }
  if (seen.has("title")) {
    lines.push(t("Give the new document a title."));
  }
  if (seen.has("collectionId")) {
    lines.push(t("Open a collection, or name one, to create the document in."));
  }
  if (seen.has("message")) {
    lines.push(t("What should the notification say?"));
  }
  if (seen.has("heading") || seen.has("text")) {
    lines.push(t("I couldn't draft that section — try describing it differently."));
  }
  // kms rendered NOTHING here and simply disabled the button. Slate says why. The wording stays
  // "document" although the server now resolves research and collections too (S1): the common
  // case is still a document, and a person who asked for research sees the kind on the card.
  if (actionId === "navigate" && (seen.has("targetQuery") || seen.has("targetDocumentId"))) {
    lines.push(t("I couldn't find a document matching that."));
  }
  return lines;
}

/** Humanise a digest trigger. `everyMinutes` is minutes, and 1440 of them is a day. */
export function triggerLabel(trigger: TriggerDraft | null, t: Translate): string {
  if (!trigger) {
    return t("no schedule");
  }
  if (trigger.kind === "at") {
    return t("once");
  }
  if (trigger.kind === "on") {
    return t("when {{event}} happens", { event: trigger.onEvent });
  }
  const minutes = trigger.everyMinutes;
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? t("daily") : t("every {{count}} days", { count: days });
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? t("hourly") : t("every {{count}} hours", { count: hours });
  }
  return t("every {{count}} min", { count: minutes });
}
